import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import NowName from '../components/shared/NowName';
import { FormModal, Field, FilePicker, inputCls, btnGhost } from '../components/shared/FormKit';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import { detailLabel, detailValue } from '../components/workflow/constants';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { pqRequiredGaps, pqDateOrderError } from './WorkflowPqNew';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// Professional qualification declaration — detail page
// (docs/modules/workflow-ld-pq.md). Requester modifies (until approved);
// ld:pq:review holders approve/reject.
const CHANGE_LABELS = {
  qualificationTitle: 'Qualification',
  awardedDate: 'Awarded On',
  expiryDate: 'Expires On',
  remarks: 'Remarks',
};

const fmtChangeValue = (field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(s) ? formatDate(s) : s;
};

const WorkflowPqDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [existingRecord, setExistingRecord] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [approving, setApproving] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/ld/pq-requests/${id}`),
        fetchJSON(`/api/ld/pq-requests/${id}/events`),
      ]);
      setRequest(detail.request);
      setExistingRecord(detail.existingRecord || null);
      setCanReview(detail.canReview);
      setCanModify(detail.canModify);
      setCanDelete(detail.canDelete);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/pq-requests/${id}/approve`, { method: 'POST', body: JSON.stringify({}) });
      setApproving(false);
      setNotice('Declaration approved — the qualification has been recorded and the requester notified.');
      await load();
    } catch (e) {
      setError(e.message);
      setApproving(false);
    }
    finally { setActing(false); }
  };

  const resubmit = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/pq-requests/${id}/resubmit`, { method: 'POST' });
      setNotice('Declaration re-submitted — reviewers have been notified.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const hasAwaitingInfo = request && request.status === 'awaiting_info';

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (canReview && request.status === 'requested');
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: CHANGE_LABELS,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/pq"
      backLabel="Professional Qualification"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="Professional Qualification"
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          {existingRecord && request.status === 'requested' && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
              <span>
                {request.staffNameNow || request.staffName} already holds <span className="font-medium">{existingRecord.title}</span>
                {existingRecord.awardedDate ? ` (awarded ${formatDate(existingRecord.awardedDate)})` : ''} on record —
                approving would double-record it, so reject this declaration unless the record is wrong.
              </span>
            </div>
          )}

          <StatusActionsCard
            showDecider
            sides={[{
              key: 'main',
              label: null,
              status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={acting}
            events={events}
            onApprove={canReview && request.status === 'requested' ? () => setApproving(true) : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify && request.status !== 'approved' ? () => setModifying(true) : null}
            onResubmit={canModify && hasAwaitingInfo ? resubmit : null}
            onDelete={canDelete ? () => setConfirmingDelete(true) : null}
          >
            <div>
              <p className={detailLabel}>Qualification</p>
              <p className={detailValue}>{request.qualificationTitle}</p>
              {request.status === 'approved' && (
                <p className="text-xs text-green-600 mt-1">Recorded on the staff profile</p>
              )}
            </div>
          </StatusActionsCard>

          <ChangesSinceReturnCard
            events={events}
            changeLabels={CHANGE_LABELS}
            fmtChangeValue={fmtChangeValue}
          />

          <SectionCard title="Declaration Details">
            <DetailGrid>
              <DetailItem label="Staff"><NowName value={request.staffNameNow || request.staffName} now={request.staffNameNow} /></DetailItem>
              <DetailItem label="Qualification" field="qualificationTitle">{request.qualificationTitle}</DetailItem>
              <DetailItem label="Awarded On" field="awardedDate">{request.awardedDate ? formatDate(request.awardedDate) : '—'}</DetailItem>
              {/* Only qualifications that need renewing carry one, so the
                  row is there whenever the declaration states a date. */}
              {request.expiryDate && (
                <DetailItem label="Expires On" field="expiryDate">{formatDate(request.expiryDate)}</DetailItem>
              )}
              <DetailItem label="Remarks" field="remarks">{request.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          <SectionCard title="Certificates">
            <AttachmentsList
              files={request.files || []}
              downloadHref={(f) => `${API_BASE_URL}/api/ld/pq-requests/${request.id}/files/${f.id}`}
              badgeFor={() => ['Certificate', 'bg-blue-50 text-blue-700']}
            />
            <FieldCommentAnchor field="files" label="Certificates" />
          </SectionCard>

          <HistoryCard
            events={events}
            actionLabels={{ record_created: ['Qualification record created', 'bg-green-50 text-green-700'] }}
            changeLabels={CHANGE_LABELS}
            fmtChangeValue={fmtChangeValue}
          />

          {approving && (
            <FormModal
              title={`Approve ${request.requestNo}`}
              size="md"
              onClose={() => setApproving(false)}
              footer={(
                <>
                  <button type="button" onClick={() => setApproving(false)} className={btnGhost}>Cancel</button>
                  <button
                    type="button"
                    onClick={approve}
                    disabled={acting}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {acting ? 'Approving…' : 'Approve'}
                  </button>
                </>
              )}
            >
              <p className="text-sm text-slate-700">
                Approving records <span className="font-semibold">{request.qualificationTitle}</span> for{' '}
                <span className="font-semibold">{request.staffNameNow || request.staffName}</span> as a professional qualification.
              </p>
              {existingRecord && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  Already on record — approving will fail. Return the declaration for changes instead.
                </div>
              )}
            </FormModal>
          )}
          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/ld/pq-requests/${request.id}/reject`, {
                  method: 'POST',
                  body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {modifying && (
            <PqModifyModal
              request={request}
              events={events}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice(hasAwaitingInfo
                  ? 'Changes saved. Press Re-submit to send the declaration back for review.'
                  : 'Changes saved.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the declaration and its certificates. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/ld/pq-requests/${request.id}`, { method: 'DELETE' });
                navigate('/workflow/pq');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

const PqModifyModal = ({ request, events = [], onClose, onDone }) => {
  const [types, setTypes] = useState([]);
  const [qualificationTypeId, setQualificationTypeId] = useState(
    request.qualificationTypeId != null ? String(request.qualificationTypeId) : ''
  );
  const [awardedDate, setAwardedDate] = useState(request.awardedDate || '');
  const [expiryDate, setExpiryDate] = useState(request.expiryDate || '');
  const [remarks, setRemarks] = useState(request.remarks || '');
  const [files, setFiles] = useState([]);
  const { removeIds, toggle, appendTo } = useRemovableFiles();
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    fetchJSON('/api/ld/qualification-types/options')
      .then((d) => {
        if (!active) return;
        const list = d.data || [];
        // Keep the currently-picked type selectable even if since deactivated.
        if (request.qualificationTypeId != null
            && !list.some(t => String(t.id) === String(request.qualificationTypeId))) {
          list.push({
            id: request.qualificationTypeId,
            title: `${request.qualificationTitle} (inactive)`,
            // The retired row's renewal flag is not on the options feed — the
            // declaration already carrying an expiry date is the same answer.
            requiresRenewal: !!request.expiryDate,
          });
        }
        setTypes(list);
      })
      .catch(() => { if (active) setTypes([]); });
    return () => { active = false; };
  }, [request.qualificationTypeId, request.qualificationTitle, request.expiryDate]);

  // Same rule as the submission form: the date shows, and is mandatory,
  // exactly for the qualifications the catalogue marks for renewal.
  const selectedType = types.find(t => String(t.id) === String(qualificationTypeId)) || null;
  const needsExpiry = !!selectedType?.requiresRenewal;

  // The submission form's own predicates — imported, not copied, so the two
  // surfaces cannot drift apart.
  const gaps = useMemo(
    () => pqRequiredGaps({ qualificationTypeId, awardedDate, expiryDate, needsExpiry }),
    [qualificationTypeId, awardedDate, expiryDate, needsExpiry],
  );
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    const problem = pqDateOrderError({ awardedDate, expiryDate, needsExpiry });
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      if (qualificationTypeId) form.set('qualificationTypeId', qualificationTypeId);
      form.set('awardedDate', awardedDate);
      // Switching to a qualification that never lapses clears the date it carried.
      form.set('expiryDate', needsExpiry ? expiryDate : '');
      form.set('remarks', remarks);
      files.forEach(f => form.append('files', f));
      appendTo(form);
      await fetchJSON(`/api/ld/pq-requests/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="md"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      )}
    >
      <ReturnCommentsBanner events={events} />
      {(error || validation.error) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error || validation.error}</div>
      )}
      <FormValidationProvider value={validation.providerValue}>
        <Field label="Qualification" field="qualificationTitle" required>
          <select value={qualificationTypeId} onChange={e => setQualificationTypeId(e.target.value)} className={inputCls}>
            {types.map(t => (
              <option key={t.id} value={t.id}>{t.fullName ? `${t.title} — ${t.fullName}` : t.title}</option>
            ))}
          </select>
        </Field>
        <Field label="Awarded On" field="awardedDate" required>
          <input type="date" value={awardedDate} onChange={e => setAwardedDate(e.target.value)} className={inputCls} />
        </Field>
        {needsExpiry && (
          <Field label="Expires On" field="expiryDate" required hint="This qualification has to be renewed.">
            <input
              type="date"
              value={expiryDate}
              min={awardedDate || undefined}
              onChange={e => setExpiryDate(e.target.value)}
              className={inputCls}
            />
          </Field>
        )}
        <Field label="Remarks" field="remarks">
          <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
        </Field>
        <EditableAttachments
          files={request.files || []}
          removeIds={removeIds}
          onToggle={toggle}
          downloadHref={(f) => `${API_BASE_URL}/api/ld/pq-requests/${request.id}/files/${f.id}`}
          badgeFor={() => ['Certificate', 'bg-blue-50 text-blue-700']}
          label="Current Certificates"
        />
        <FieldCommentInline field="files" />
        <Field label="Add Certificates">
          <FilePicker
            multiple
            kind="docsAndImages"
            onChange={e => setFiles(Array.from(e.target.files || []))}
          />
        </Field>
      </FormValidationProvider>
    </FormModal>
  );
};

export default WorkflowPqDetail;
